import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle } from 'lucide-react';
import api from '../services/api';

/**
 * BodyTodayPanel — surfaces the Recovery slice of today's "Body & Mind"
 * check-in (soreness + fatigue + energy) on the Movement page. Soft "no data"
 * prompt when the user hasn't checked in yet.
 *
 * Listens for the `wellness:refresh` event so submitting a check-in
 * anywhere (chat, mental page, here) updates this panel live.
 */

// 1-5 self-reports → band meta. Soreness/fatigue are inverted (high = worse).
const sorenessMeta = (v) => v >= 4
  ? { color: '#f87171', label: `${v}/5 · high`, note: 'Step back from heavy load — mobility + blood flow today.' }
  : v >= 3
    ? { color: '#facc15', label: `${v}/5 · moderate`, note: 'A bit sore. Warm up well; keep load controlled.' }
    : { color: '#a3e635', label: `${v}/5 · low`, note: 'Muscles feel good — full programming on the table.' };

const fatigueMeta = (v) => v >= 4
  ? { color: '#f87171', label: `${v}/5 · wiped`, note: 'Running on empty — ease intensity, prioritise rest.' }
  : v >= 3
    ? { color: '#facc15', label: `${v}/5 · tired`, note: 'Some fatigue. Keep it moderate.' }
    : { color: '#a3e635', label: `${v}/5 · fresh`, note: 'Good capacity today.' };

const energyMeta = (v) => v >= 4
  ? { color: '#a3e635', label: `${v}/5 · high`, note: "You can push a touch harder if you're feeling it." }
  : v >= 3
    ? { color: '#CCFF00', label: `${v}/5 · steady`, note: 'Match it — a standard session works.' }
    : { color: '#fb923c', label: `${v}/5 · low`, note: "Halve any session you'd usually do." };

const BodyTodayPanel = () => {
  const [checkin, setCheckin] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.getTodayCheckin();
      setCheckin(r.data?.checkin || null);
    } catch { setCheckin(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('wellness:refresh', h);
    return () => window.removeEventListener('wellness:refresh', h);
  }, [load]);

  if (loading) return null;

  const soreness = typeof checkin?.muscle_soreness === 'number' ? sorenessMeta(checkin.muscle_soreness) : null;
  const fatigue = typeof checkin?.fatigue === 'number' ? fatigueMeta(checkin.fatigue) : null;
  const energy = typeof checkin?.body_energy === 'number' ? energyMeta(checkin.body_energy) : null;
  const slices = [
    soreness && { key: 'soreness', label: 'SORENESS', m: soreness },
    fatigue && { key: 'fatigue', label: 'FATIGUE', m: fatigue },
    energy && { key: 'energy', label: 'ENERGY FOR MOVEMENT', m: energy },
  ].filter(Boolean);

  return (
    <div className="hpme-card p-5" data-testid="body-today-panel">
      <div className="hpme-card-corner" />
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-primary" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">YOUR BODY TODAY</span>
      </div>
      {slices.length > 0 ? (
        <div className="grid sm:grid-cols-3 gap-4">
          {slices.map(s => (
            <div key={s.key} className="border-l-2 pl-3 py-1" style={{ borderColor: s.m.color }} data-testid={`body-${s.key}-slice`}>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary mb-0.5">{s.label}</p>
              <p className="font-secondary font-bold text-lg" style={{ color: s.m.color }}>{s.m.label}</p>
              <p className="text-xs text-zinc-300 leading-relaxed mt-1">{s.m.note}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-zinc-500 leading-relaxed flex items-center gap-2" data-testid="body-today-empty">
          <AlertTriangle className="w-3.5 h-3.5" />
          You haven't logged a body check-in today. Pop over to <Link to="/dashboard" className="text-primary hover:underline">the Dashboard check-in</Link> — your movement plan adjusts to it.
        </p>
      )}
    </div>
  );
};

export default BodyTodayPanel;
